import { QuestionCircleOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Steps,
  Switch,
  TimePicker,
  Tooltip,
  Typography,
  notification,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import commitPendingInput from "../../utils/commitPendingInput";
import useApi from "../../utils/useApi";
import PlanoPanel from "./PlanoPanel";
import SeletorDeDestinatarios, {
  inscricoesParaEnvio,
  ordemInicial,
} from "./SeletorDeDestinatarios";
import SeletorDeFiliais from "./SeletorDeFiliais";
import { describeError } from "./status";

const { Text } = Typography;

const CAMPOS_DO_PASSO_1 = [
  "client",
  "name",
  "branches",
  "model",
  "periodicidade",
  "janela_dias",
  "dia_execucao",
  "hora_execucao",
  "tipo_faturamento",
  "pedido",
];

const FAIXA = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0 20px",
  alignItems: "flex-end",
};

const FAIXA_COM_FUNDO = {
  ...FAIXA,
  background: "#f3f6fa",
  border: "1px solid #e3e8ef",
  borderRadius: 8,
  padding: "16px 16px 0",
  marginBottom: 24,
};

function assinaturaDoPlano(valores) {
  return JSON.stringify({
    client: valores.client,
    pedido: (valores.pedido || "").trim(),
    periodicidade: valores.periodicidade,
    janela_dias: valores.janela_dias,
    tipo_faturamento: valores.tipo_faturamento,
  });
}

export default function RoutineFormModal({
  open,
  onClose,
  onSaved,
  catalog,
  editing,
  onContatoSalvo,
}) {
  const {
    createAiReportRoutine,
    updateAiReportRoutine,
    planAiReport,
    sampleAiReportVersion,
  } = useApi();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [passo, setPasso] = useState(0);
  const [plano, setPlano] = useState(null);
  const [assinatura, setAssinatura] = useState(null);
  const [planejando, setPlanejando] = useState(false);
  const [amostra, setAmostra] = useState(null);
  const [amostraLoading, setAmostraLoading] = useState(false);
  const [agendarMesmoAssim, setAgendarMesmoAssim] = useState(false);
  const [filialPrevia, setFilialPrevia] = useState(null);
  const [ordem, setOrdem] = useState([]);
  const clientId = Form.useWatch("client", form);
  const periodicidade = Form.useWatch("periodicidade", form);
  const branchesEscolhidas = Form.useWatch("branches", form);
  const valoresAtuais = Form.useWatch([], form);
  const contatosDoCliente = (catalog?.telefones || []).filter(
    (contato) => contato.client_id === clientId
  );

  useEffect(() => {
    if (!open) return;
    setOrdem(
      ordemInicial(contatosDoCliente, form.getFieldValue("inscricoes") || [])
    );
  }, [open, clientId, catalog]);

  useEffect(() => {
    if (!open) return;
    setPasso(0);
    setPlano(null);
    setAssinatura(null);
    setAmostra(null);
    setAgendarMesmoAssim(false);
    setFilialPrevia(null);
    form.resetFields();
    if (editing) {
      form.setFieldsValue({
        ...editing,
        pedido: editing.pedido || "",
        hora_execucao: dayjs(editing.hora_execucao, "HH:mm:ss"),
        inscricoes: editing.inscricoes || [],
      });
    } else {
      form.setFieldsValue({
        pedido: "",
        client:
          catalog?.clients?.length === 1 ? catalog.clients[0].id : undefined,
        periodicidade: "semanal",
        janela_dias: 7,
        dia_execucao: 0,
        hora_execucao: dayjs("08:00", "HH:mm"),
        tipo_faturamento: "presencial",
        is_active: false,
        branches: [],
        inscricoes: [],
        entrega_automatica: false,
      });
    }
  }, [open, editing]);

  const clientes = (catalog?.clients || []).map((c) => ({
    value: c.id,
    label: `${c.name}${c.tipo ? ` (${c.tipo})` : ""}`,
  }));
  const filiais = (catalog?.branches || [])
    .filter((b) => !clientId || b.client_id === clientId)
    .map((b) => ({
      value: b.id,
      label: `${b.name}${b.store_code ? ` (loja ${b.store_code})` : ""}`,
    }));
  const filiaisDoCliente = (catalog?.branches || [])
    .filter((b) => b.client_id === clientId)
    .map((b) => ({ value: b.id, nome: b.name, loja: b.store_code }));
  const filiaisDaRotina = filiaisDoCliente
    .filter((f) => (branchesEscolhidas || []).includes(f.value))
    .map((f) => ({
      id: f.value,
      nome: f.nome,
      rotulo: f.nome,
    }));
  const filiaisDaPrevia = filiais.filter((f) =>
    (branchesEscolhidas || []).includes(f.value)
  );
  const filialDaPrevia =
    filialPrevia && filiaisDaPrevia.some((f) => f.value === filialPrevia)
      ? filialPrevia
      : filiaisDaPrevia[0]?.value;
  const planoEmUso = plano || (editing?.plano ? editing.plano : null);
  const rotinaDeTemplate = Boolean(
    editing && !editing.pedido && !editing.plano
  );
  const pedidoDigitado = (valoresAtuais?.pedido || "").trim();
  const pedidoPlanejadoMudou = plano
    ? assinatura !== assinaturaDoPlano(valoresAtuais || {})
    : Boolean(editing?.pedido) && pedidoDigitado !== editing.pedido.trim();

  const bloqueioDoPedido = () => {
    if (rotinaDeTemplate && !plano && !pedidoDigitado) return null;
    if (!plano && !(editing?.pedido || editing?.plano)) {
      return "Planeje o pedido antes de salvar.";
    }
    if (pedidoPlanejadoMudou) {
      return "O pedido mudou depois do último plano. Planeje de novo.";
    }
    if (!plano) return null;
    if ((plano.achados || []).length || (amostra?.achados || []).length) {
      return "O plano ou a amostra citam nomes fora do escopo da loja.";
    }
    if ((plano.nao_atendido || []).length && !agendarMesmoAssim) {
      return "Há trechos do pedido sem dado. Marque agendar mesmo assim ou ajuste o pedido.";
    }
    return null;
  };

  const planejar = async () => {
    await commitPendingInput();
    let valores;
    try {
      valores = await form.validateFields(["client", "model", "pedido"]);
    } catch {
      return;
    }
    const todos = form.getFieldsValue(true);
    if (!filialDaPrevia) {
      notification.error({
        message: "Marque as filiais da rotina antes de planejar.",
      });
      return;
    }
    setPlanejando(true);
    setAmostra(null);
    try {
      const resposta = await planAiReport({
        client_id: valores.client,
        branch_id: filialDaPrevia,
        routine_id: editing?.id,
        periodicidade: todos.periodicidade,
        janela_dias: todos.janela_dias || 7,
        tipo_faturamento: todos.tipo_faturamento,
        pedido: valores.pedido,
        model_id: valores.model,
      });
      setPlano(resposta);
      setAssinatura(assinaturaDoPlano(todos));
      setAgendarMesmoAssim(false);
    } catch (err) {
      notification.error({
        message: "O planejador não conseguiu montar o plano.",
        description: describeError(err, "Tente de novo."),
      });
    } finally {
      setPlanejando(false);
    }
  };

  const gerarAmostra = async () => {
    setAmostraLoading(true);
    try {
      setAmostra(await sampleAiReportVersion(plano.versao_id));
    } catch (err) {
      notification.error({
        message: "Erro ao gerar a amostra.",
        description: describeError(err, ""),
      });
    } finally {
      setAmostraLoading(false);
    }
  };

  const avancar = async () => {
    await commitPendingInput();
    try {
      await form.validateFields(CAMPOS_DO_PASSO_1);
    } catch {
      return;
    }
    const bloqueio = bloqueioDoPedido();
    if (bloqueio) {
      notification.warning({ message: bloqueio });
      return;
    }
    setPasso(1);
  };

  const modelos = (catalog?.models || []).map((m) => ({
    value: m.id,
    label: `${m.provider}/${m.model_id}`,
  }));
  const diasSemana = catalog?.choices?.dia_semana || [];

  const handleSave = async () => {
    await commitPendingInput();
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      const campo = err?.errorFields?.[0]?.name?.[0];
      if (CAMPOS_DO_PASSO_1.includes(campo)) setPasso(0);
      return;
    }
    const bloqueio = bloqueioDoPedido();
    if (bloqueio) {
      setPasso(0);
      notification.warning({ message: bloqueio });
      return;
    }
    const { itens, semFilial } = inscricoesParaEnvio(
      values.inscricoes,
      values.branches || []
    );
    if (semFilial.length) {
      notification.warning({
        message: `Marque ao menos uma filial para ${semFilial.join(", ")}.`,
      });
      return;
    }
    const { pedido, prompt_template, ...resto } = values;
    const payload = {
      ...resto,
      inscricoes: itens,
      hora_execucao: values.hora_execucao.format("HH:mm"),
    };
    if (plano) {
      payload.versao_id = plano.versao_id;
      payload.agendar_mesmo_assim = agendarMesmoAssim;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateAiReportRoutine(editing.id, payload);
        notification.success({ message: "Rotina atualizada." });
      } else {
        await createAiReportRoutine(payload);
        notification.success({
          message: "Rotina criada.",
          description:
            "Ela está inativa e sem envio automático. Confira a amostra e ative na edição quando quiser que ela rode.",
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      notification.error({
        message: "Erro ao salvar rotina.",
        description: describeError(err, "Verifique os campos."),
      });
    } finally {
      setSaving(false);
    }
  };

  const pendencia = passo === 0 ? bloqueioDoPedido() : null;

  return (
    <Modal
      open={open}
      title={editing ? `Editar rotina: ${editing.name}` : "Nova rotina"}
      onCancel={onClose}
      width={1100}
      destroyOnClose
      footer={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text
            type="warning"
            style={{ marginRight: "auto", textAlign: "left" }}>
            {pendencia}
          </Text>
          <Button onClick={onClose}>Cancelar</Button>
          {passo === 0 ? (
            <Button type="primary" onClick={avancar}>
              Próximo: quem recebe
            </Button>
          ) : (
            <>
              <Button onClick={() => setPasso(0)}>Voltar</Button>
              <Button type="primary" onClick={handleSave} loading={saving}>
                {editing ? "Salvar rotina" : "Criar rotina"}
              </Button>
            </>
          )}
        </div>
      }>
      <Steps
        current={passo}
        onChange={(novo) => (novo === 0 ? setPasso(0) : avancar())}
        style={{ margin: "16px 0 24px" }}
        items={[
          { title: "O que o relatório traz e quando sai" },
          { title: "Quem recebe" },
        ]}
      />
      <Form form={form} layout="vertical">
        <div style={{ display: passo === 0 ? "block" : "none" }}>
          <div style={FAIXA}>
            <Form.Item
              name="client"
              label="Cliente"
              style={{ width: 260 }}
              rules={[{ required: true, message: "Selecione o cliente." }]}>
              <Select
                options={clientes}
                disabled={Boolean(editing)}
                onChange={() =>
                  form.setFieldsValue({ branches: [], inscricoes: [] })
                }
              />
            </Form.Item>
            <Form.Item
              name="name"
              label="Nome"
              style={{ flex: 1, minWidth: 240 }}
              rules={[{ required: true, message: "Informe o nome." }]}>
              <Input maxLength={120} />
            </Form.Item>
            <Form.Item label=" " colon={false}>
              {editing ? (
                <Space size={24}>
                  <Space size={8}>
                    <Form.Item name="is_active" valuePropName="checked" noStyle>
                      <Switch aria-label="Ativa" />
                    </Form.Item>
                    <Text>Ativa</Text>
                  </Space>
                  <Space size={8}>
                    <Form.Item
                      name="entrega_automatica"
                      valuePropName="checked"
                      noStyle>
                      <Switch aria-label="Envio automático" />
                    </Form.Item>
                    <Text>Envio automático</Text>
                    <Tooltip title="Envia a cada contato assim que o relatório das filiais dele fica pronto. Relatório com achado de guardrail não sai. Desligado, o relatório fica pronto e alguém envia pelo botão Enviar da execução.">
                      <QuestionCircleOutlined
                        aria-label="Sobre o envio automático"
                        style={{ color: "rgba(0, 0, 0, 0.45)" }}
                      />
                    </Tooltip>
                  </Space>
                </Space>
              ) : (
                <Text type="secondary">
                  Nasce inativa e sem envio automático. Ative na edição.
                </Text>
              )}
            </Form.Item>
          </div>
          {catalog && !catalog.entrega_configurada && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
              message="O envio por WhatsApp ainda não está configurado. Configure na aba WhatsApp desta página; até lá nada é enviado."
            />
          )}
          <div style={FAIXA_COM_FUNDO}>
            <Form.Item
              name="periodicidade"
              label="Envia"
              style={{ width: 130 }}>
              <Select
                options={catalog?.choices?.periodicidade?.map((c) => ({
                  value: c.value,
                  label: c.label,
                }))}
                onChange={(v) =>
                  form.setFieldsValue({ dia_execucao: v === "mensal" ? 1 : 0 })
                }
              />
            </Form.Item>
            <Form.Item
              name="dia_execucao"
              label={periodicidade === "mensal" ? "Dia do mês" : "Dia"}
              style={{ width: 130 }}
              rules={[{ required: true, message: "Informe o dia." }]}>
              {periodicidade === "mensal" ? (
                <InputNumber min={1} max={31} style={{ width: "100%" }} />
              ) : (
                <Select
                  options={diasSemana.map((d) => ({
                    value: d.value,
                    label: d.label,
                  }))}
                />
              )}
            </Form.Item>
            <Form.Item
              name="hora_execucao"
              label="Hora"
              style={{ width: 110 }}
              rules={[{ required: true, message: "Informe a hora." }]}>
              <TimePicker
                format="HH:mm"
                minuteStep={5}
                needConfirm={false}
                showNow={false}
                style={{ width: "100%" }}
              />
            </Form.Item>
            {periodicidade !== "mensal" && (
              <Form.Item
                name="janela_dias"
                label="Período (dias)"
                style={{ width: 120 }}
                rules={[{ required: true, message: "Informe o período." }]}>
                <InputNumber min={1} max={90} style={{ width: "100%" }} />
              </Form.Item>
            )}
            <Form.Item
              name="tipo_faturamento"
              label="Faturamento"
              style={{ width: 140 }}
              tooltip="Vale para todas as fontes do plano.">
              <Select
                options={catalog?.choices?.tipo_faturamento?.map((c) => ({
                  value: c.value,
                  label: c.label,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="model"
              label="Modelo de IA"
              style={{ width: 220 }}
              tooltip="O mesmo modelo planeja o pedido (com raciocínio alto) e redige cada envio."
              rules={[{ required: true, message: "Selecione o modelo." }]}>
              <Select options={modelos} />
            </Form.Item>
          </div>
          <Form.Item
            name="branches"
            label="Filiais da rotina"
            tooltip="Um relatório por filial. Quem recebe de várias filiais ganha tudo numa mensagem só. Filial criada depois só entra se for marcada aqui."
            rules={[
              {
                type: "array",
                min: 1,
                message: "Escolha ao menos uma filial.",
              },
            ]}>
            <SeletorDeFiliais filiais={filiaisDoCliente} />
          </Form.Item>
          {rotinaDeTemplate && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Esta rotina ainda usa o template ${editing.prompt_template_slug} e segue funcionando assim.`}
              description="Para passar para pedido em texto livre, descreva o relatório abaixo e planeje. Sem pedido, salvar só muda agenda, filiais e destinatários."
            />
          )}
          <Form.Item
            name="pedido"
            label="Pedido"
            tooltip="Descreva como falaria com um analista. O planejador escolhe as fontes uma vez e o plano fica congelado até você planejar de novo."
            rules={[
              {
                required: !rotinaDeTemplate,
                whitespace: true,
                message: "Descreva o relatório.",
              },
            ]}>
            <Input.TextArea
              rows={3}
              maxLength={2000}
              placeholder="Ex.: os 4 produtos mais vendidos em quantidade e os 4 de maior margem, comparando com a semana anterior."
            />
          </Form.Item>
          <Space wrap style={{ marginBottom: 16 }}>
            <Text>Prévia com</Text>
            <Select
              style={{ minWidth: 260 }}
              options={filiaisDaPrevia}
              value={filialDaPrevia}
              onChange={setFilialPrevia}
              placeholder="Filial da pré-visualização"
            />
            <Button type="primary" onClick={planejar} loading={planejando}>
              {planoEmUso ? "Planejar de novo" : "Planejar"}
            </Button>
          </Space>
          {!planoEmUso && !planejando && (
            <Alert
              type="info"
              showIcon
              message="Planeje para ver as fontes escolhidas, os avisos e uma amostra antes de agendar."
            />
          )}
          <PlanoPanel
            plano={planoEmUso}
            desatualizado={pedidoPlanejadoMudou}
            agendarMesmoAssim={agendarMesmoAssim}
            onAgendarMesmoAssim={plano ? setAgendarMesmoAssim : null}
            amostra={amostra}
            amostraLoading={amostraLoading}
            onAmostra={plano ? gerarAmostra : null}
          />
        </div>
        <div style={{ display: passo === 1 ? "block" : "none" }}>
          <Form.Item name="inscricoes" noStyle>
            <SeletorDeDestinatarios
              key={clientId}
              contatos={contatosDoCliente}
              filiais={filiaisDaRotina}
              ordem={ordem}
              cliente={(catalog?.clients || []).find((c) => c.id === clientId)}
              filiaisDoCliente={filiaisDoCliente.map((f) => ({
                id: f.value,
                name: f.nome,
              }))}
              onContatoSalvo={onContatoSalvo}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}
